import { useEffect } from 'react'

interface Star { x: number; y: number; radius: number; alpha: number; velocity: number }

export function useVisualEffects() {
  useEffect(() => {
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches
    const revealElements = document.querySelectorAll<HTMLElement>('.reveal')
    const observer = new IntersectionObserver((entries) => entries.forEach((entry) => {
      if (entry.isIntersecting) { entry.target.classList.add('show'); observer.unobserve(entry.target) }
    }), { threshold: 0.12, rootMargin: '0px 0px -40px' })
    revealElements.forEach((element, index) => {
      element.style.transitionDelay = `${Math.min(index % 4, 3) * 80}ms`
      observer.observe(element)
    })

    const progress = document.querySelector<HTMLElement>('.scroll-progress span')
    const updateScroll = () => {
      const max = document.documentElement.scrollHeight - innerHeight
      if (progress) progress.style.width = `${max > 0 ? (scrollY / max) * 100 : 0}%`
    }
    addEventListener('scroll', updateScroll, { passive: true }); updateScroll()

    const cleanups: Array<() => void> = []
    if (!reducedMotion && matchMedia('(pointer:fine)').matches) {
      const pointer = (event: PointerEvent) => {
        document.body.style.setProperty('--mouse-x', `${event.clientX}px`)
        document.body.style.setProperty('--mouse-y', `${event.clientY}px`)
      }
      addEventListener('pointermove', pointer, { passive: true })
      cleanups.push(() => removeEventListener('pointermove', pointer))

      document.querySelectorAll<HTMLElement>('.tilt-card').forEach((card) => {
        const move = (event: PointerEvent) => {
          const rect = card.getBoundingClientRect()
          const x = (event.clientX - rect.left) / rect.width
          const y = (event.clientY - rect.top) / rect.height
          card.style.setProperty('--card-x', `${x * 100}%`)
          card.style.setProperty('--card-y', `${y * 100}%`)
          card.style.transform = `perspective(900px) rotateX(${(0.5 - y) * 5}deg) rotateY(${(x - 0.5) * 7}deg) translateY(-4px)`
        }
        const leave = () => { card.style.transform = '' }
        card.addEventListener('pointermove', move); card.addEventListener('pointerleave', leave)
        cleanups.push(() => { card.removeEventListener('pointermove', move); card.removeEventListener('pointerleave', leave) })
      })
    }

    const ripple = (event: PointerEvent) => {
      if (event.button !== 0) return
      const element = document.createElement('span'); element.className = 'click-ripple'
      element.style.left = `${event.clientX}px`; element.style.top = `${event.clientY}px`
      document.body.appendChild(element); element.addEventListener('animationend', () => element.remove())
    }
    addEventListener('pointerdown', ripple)

    const canvas = document.querySelector<HTMLCanvasElement>('#stars')
    const context = canvas?.getContext('2d')
    let stars: Star[] = []; let frameId = 0
    const resize = () => {
      if (!canvas || !context) return
      const density = Math.min(devicePixelRatio || 1, 2)
      canvas.width = innerWidth * density; canvas.height = innerHeight * density
      context.setTransform(density, 0, 0, density, 0, 0)
      stars = Array.from({ length: Math.floor(innerWidth * innerHeight / 11000) }, () => ({
        x: Math.random() * innerWidth, y: Math.random() * innerHeight,
        radius: Math.random() * 1.1 + 0.2, alpha: Math.random() * 0.65 + 0.15, velocity: Math.random() * 0.08 + 0.02,
      }))
    }
    const frame = () => {
      if (!context || reducedMotion) return
      context.clearRect(0, 0, innerWidth, innerHeight)
      stars.forEach((star) => {
        star.y = star.y > innerHeight ? 0 : star.y + star.velocity
        context.beginPath(); context.fillStyle = `rgba(115,205,255,${star.alpha})`
        context.arc(star.x, star.y, star.radius, 0, Math.PI * 2); context.fill()
      })
      frameId = requestAnimationFrame(frame)
    }
    resize(); frame(); addEventListener('resize', resize, { passive: true })

    return () => {
      observer.disconnect(); removeEventListener('scroll', updateScroll); removeEventListener('pointerdown', ripple)
      removeEventListener('resize', resize); cancelAnimationFrame(frameId); cleanups.forEach((cleanup) => cleanup())
    }
  }, [])
}
